<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import LaisOperationalPage from 'dashboard/components-next/LaisOperationalPage.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import WhatsappCatalogConnectionBar from '../../components/WhatsappCatalogConnectionBar.vue';
import { useWhatsappCatalog } from '../../composables/useWhatsappCatalog';
import {
  catalogProductImage,
  catalogProductPrice,
  catalogProductStatus,
} from '../../utils/whatsappCatalog';

const route = useRoute();
const router = useRouter();
const account = useAccount();
const selectedConnectionId = ref(route.query.connection_id || '');
const search = ref('');
const visibility = ref('all');
const initialized = ref(false);
const pageSizeOptions = [20, 50, 100];
let refreshTimer = null;

const {
  connections,
  products,
  isLoadingConnections,
  isLoadingProducts,
  error,
  lastUpdatedAt,
  isConnected,
  canManage,
  pageSize,
  currentPage,
  hasNextPage,
  hasPreviousPage,
  visibleProducts,
  hiddenProducts,
  loadConnections,
  refreshProducts,
  loadNextPage,
  loadPreviousPage,
  setPageSize,
} = useWhatsappCatalog(selectedConnectionId);

const filteredProducts = computed(() => {
  const query = search.value.trim().toLowerCase();
  return products.value.filter(product => {
    const matchesVisibility =
      visibility.value === 'all' ||
      (visibility.value === 'visible' && !product.is_hidden) ||
      (visibility.value === 'hidden' && product.is_hidden);
    const matchesSearch =
      !query ||
      [product.name, product.description, product.retailer_id]
        .filter(Boolean)
        .some(value => value.toString().toLowerCase().includes(query));

    return matchesVisibility && matchesSearch;
  });
});

const connectionQuery = computed(() =>
  selectedConnectionId.value
    ? { connection_id: selectedConnectionId.value }
    : {}
);
const createRoute = computed(() =>
  account.accountScopedRoute(
    'minha_lais_whatsapp_catalog_new',
    {},
    connectionQuery.value
  )
);
const connectionRoute = computed(() =>
  account.accountScopedRoute(
    'minha_lais_index',
    {},
    { tab: 'whatsapp', whatsapp_subtab: 'conexao_qr' }
  )
);
const productRoute = product =>
  account.accountScopedRoute(
    'minha_lais_whatsapp_catalog_show',
    { productId: product.id },
    connectionQuery.value
  );
const editRoute = product =>
  account.accountScopedRoute(
    'minha_lais_whatsapp_catalog_edit',
    { productId: product.id },
    connectionQuery.value
  );

const syncConnectionQuery = () => {
  const query = { ...route.query };
  if (selectedConnectionId.value)
    query.connection_id = selectedConnectionId.value;
  else delete query.connection_id;
  router.replace({ query }).catch(() => {});
};

const stopRefresh = () => {
  if (refreshTimer) window.clearInterval(refreshTimer);
  refreshTimer = null;
};

const startRefresh = () => {
  stopRefresh();
  if (!selectedConnectionId.value || !isConnected.value) return;
  refreshTimer = window.setInterval(
    () => refreshProducts({ silent: true }),
    15000
  );
};

const reload = async (options = {}) => {
  await refreshProducts({ reset: options?.reset === true });
  startRefresh();
};

const changePageSize = async event => {
  await setPageSize(event.target.value);
  startRefresh();
};

const goToNextPage = async () => {
  await loadNextPage();
  startRefresh();
};

const goToPreviousPage = async () => {
  await loadPreviousPage();
  startRefresh();
};

watch(selectedConnectionId, async (value, previousValue) => {
  if (!initialized.value || String(value) === String(previousValue)) return;
  syncConnectionQuery();
  await reload({ reset: true });
});

watch(isConnected, startRefresh);

onMounted(async () => {
  await loadConnections();
  initialized.value = true;
  syncConnectionQuery();
  if (selectedConnectionId.value) await reload({ reset: true });
});

onUnmounted(stopRefresh);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <LaisOperationalPage
    title="Catálogo do WhatsApp"
    helper="Gerencie os produtos exibidos na conta conectada com uma experiência inspirada no catálogo do WhatsApp Business."
    :breadcrumb="[
      {
        label: 'Minha Lais',
        to: account.accountScopedRoute('minha_lais_index'),
      },
      { label: 'WhatsApp' },
      { label: 'Catálogo' },
    ]"
    :loading="isLoadingConnections || isLoadingProducts"
    :error="error"
    :last-updated-at="lastUpdatedAt?.toLocaleString('pt-BR')"
    @retry="reload"
  >
    <template #actions>
      <LaisPrimaryButton
        icon="i-lucide-plus"
        :disabled="!canManage || !isConnected"
        @click="router.push(createRoute)"
      >
        Adicionar produto
      </LaisPrimaryButton>
    </template>

    <template #metrics>
      <div class="grid gap-3 sm:grid-cols-3">
        <div class="rounded-lais border border-lais-border-soft bg-white p-4">
          <p
            class="text-[11px] font-semibold uppercase tracking-wide text-lais-text-muted"
          >
            Nesta página
          </p>
          <p class="mt-1 text-[26px] font-bold text-lais-text">
            {{ products.length }}
          </p>
        </div>
        <div class="rounded-lais border border-lais-border-soft bg-white p-4">
          <p
            class="text-[11px] font-semibold uppercase tracking-wide text-lais-text-muted"
          >
            Publicados
          </p>
          <p class="mt-1 text-[26px] font-bold text-emerald-700">
            {{ visibleProducts.length }}
          </p>
        </div>
        <div class="rounded-lais border border-lais-border-soft bg-white p-4">
          <p
            class="text-[11px] font-semibold uppercase tracking-wide text-lais-text-muted"
          >
            Ocultos
          </p>
          <p class="mt-1 text-[26px] font-bold text-amber-700">
            {{ hiddenProducts.length }}
          </p>
        </div>
      </div>
    </template>

    <template #filters>
      <div class="grid gap-3">
        <WhatsappCatalogConnectionBar
          v-model="selectedConnectionId"
          :connections="connections"
          :connected="isConnected"
          @refresh="reload"
        />
        <div
          class="flex flex-col gap-3 rounded-lais border border-lais-border-soft bg-white p-4 lg:flex-row lg:items-center lg:justify-between"
        >
          <label class="relative min-w-0 flex-1">
            <span
              class="i-lucide-search pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-lais-text-muted"
            />
            <input
              v-model="search"
              class="h-11 w-full rounded-lais-sm border border-lais-border bg-white pl-10 pr-3 text-[13px] text-lais-text outline-none focus:border-lais-primary focus:ring-2 focus:ring-lais-primary/10"
              placeholder="Buscar nesta página por nome, descrição ou SKU"
            />
          </label>
          <div class="flex flex-col gap-3 sm:flex-row sm:items-center">
            <label
              class="flex items-center gap-2 text-[12px] text-lais-text-muted"
            >
              <span class="whitespace-nowrap font-semibold">Por página</span>
              <select
                :value="pageSize"
                class="h-10 rounded-lais-sm border border-lais-border bg-white px-3 text-[12px] font-semibold text-lais-text outline-none focus:border-lais-primary focus:ring-2 focus:ring-lais-primary/10"
                :disabled="isLoadingProducts"
                @change="changePageSize"
              >
                <option
                  v-for="size in pageSizeOptions"
                  :key="size"
                  :value="size"
                >
                  {{ size }} anúncios
                </option>
              </select>
            </label>
            <div
              class="flex rounded-lais-sm border border-lais-border bg-lais-bg-card p-1"
            >
              <button
                v-for="filter in [
                  { id: 'all', label: 'Todos' },
                  { id: 'visible', label: 'Publicados' },
                  { id: 'hidden', label: 'Ocultos' },
                ]"
                :key="filter.id"
                type="button"
                class="rounded-lais-sm px-3 py-2 text-[12px] font-semibold transition"
                :class="
                  visibility === filter.id
                    ? 'bg-white text-lais-primary shadow-sm'
                    : 'text-lais-text-muted hover:text-lais-text'
                "
                @click="visibility = filter.id"
              >
                {{ filter.label }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </template>

    <div
      v-if="!connections.length"
      class="rounded-lais border border-dashed border-lais-border bg-white px-6 py-14 text-center"
    >
      <span
        class="i-lucide-message-circle-off mx-auto block size-12 text-lais-primary"
      />
      <h2 class="mt-4 text-[18px] font-semibold text-lais-text">
        Conecte uma conta do WhatsApp
      </h2>
      <p
        class="mx-auto mt-2 max-w-lg text-[13px] leading-6 text-lais-text-body"
      >
        O catálogo é administrado pela sessão vinculada por QR code.
      </p>
      <RouterLink
        :to="connectionRoute"
        class="mt-5 inline-flex h-10 items-center rounded-lais bg-lais-primary px-4 text-[13px] font-semibold text-white"
      >
        Abrir conexão QR
      </RouterLink>
    </div>

    <div
      v-else-if="!isConnected"
      class="rounded-lais border border-amber-200 bg-amber-50 px-6 py-10 text-center"
    >
      <span class="i-lucide-wifi-off mx-auto block size-10 text-amber-700" />
      <h2 class="mt-3 text-[17px] font-semibold text-amber-950">
        Esta conta está desconectada
      </h2>
      <p class="mx-auto mt-2 max-w-lg text-[13px] leading-6 text-amber-900">
        Reconecte a sessão para listar e alterar produtos.
      </p>
      <RouterLink
        :to="connectionRoute"
        class="mt-4 inline-flex h-10 items-center rounded-lais bg-amber-800 px-4 text-[13px] font-semibold text-white"
      >
        Reconectar WhatsApp
      </RouterLink>
    </div>

    <div
      v-else-if="filteredProducts.length"
      class="grid gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4"
    >
      <article
        v-for="product in filteredProducts"
        :key="product.id"
        class="group overflow-hidden rounded-lais border border-lais-border-soft bg-white shadow-sm transition hover:-translate-y-0.5 hover:border-lais-primary/30 hover:shadow-lais-soft"
      >
        <RouterLink :to="productRoute(product)" class="block">
          <div class="relative aspect-[4/3] overflow-hidden bg-lais-bg-card">
            <img
              v-if="catalogProductImage(product)"
              :src="catalogProductImage(product)"
              :alt="product.name"
              class="h-full w-full object-cover transition duration-300 group-hover:scale-[1.02]"
            />
            <div
              v-else
              class="grid h-full place-items-center text-lais-text-muted"
            >
              <span class="i-lucide-image size-10" />
            </div>
            <span
              class="absolute left-3 top-3 rounded-full px-2.5 py-1 text-[10px] font-bold"
              :class="catalogProductStatus(product).className"
            >
              {{ catalogProductStatus(product).label }}
            </span>
          </div>
          <div class="p-4">
            <h2 class="truncate text-[15px] font-semibold text-lais-text">
              {{ product.name }}
            </h2>
            <p class="mt-1 text-[15px] font-bold text-lais-primary">
              {{ catalogProductPrice(product) }}
            </p>
            <p
              class="mt-2 line-clamp-2 min-h-10 text-[12px] leading-5 text-lais-text-body"
            >
              {{ product.description || 'Sem descrição.' }}
            </p>
            <p
              v-if="product.retailer_id"
              class="mt-3 truncate text-[11px] text-lais-text-muted"
            >
              SKU: {{ product.retailer_id }}
            </p>
          </div>
        </RouterLink>
        <div class="flex border-t border-lais-border-soft">
          <RouterLink
            :to="productRoute(product)"
            class="flex h-10 flex-1 items-center justify-center gap-2 text-[12px] font-semibold text-lais-text-body hover:bg-lais-bg-card hover:text-lais-primary"
          >
            <span class="i-lucide-eye size-3.5" /> Ver detalhes
          </RouterLink>
          <RouterLink
            :to="editRoute(product)"
            class="flex h-10 flex-1 items-center justify-center gap-2 border-l border-lais-border-soft text-[12px] font-semibold text-lais-text-body hover:bg-lais-bg-card hover:text-lais-primary"
          >
            <span class="i-lucide-pencil size-3.5" /> Editar
          </RouterLink>
        </div>
      </article>
    </div>

    <div
      v-else-if="products.length"
      class="rounded-lais border border-dashed border-lais-border bg-white px-6 py-12 text-center"
    >
      <span
        class="i-lucide-search-x mx-auto block size-10 text-lais-text-muted"
      />
      <h2 class="mt-3 text-[16px] font-semibold text-lais-text">
        Nenhum produto encontrado
      </h2>
      <p class="mt-1 text-[13px] text-lais-text-body">
        Tente alterar a busca ou o filtro de visibilidade.
      </p>
    </div>

    <div
      v-else
      class="rounded-lais border border-dashed border-lais-border bg-white px-6 py-14 text-center"
    >
      <span
        class="i-lucide-shopping-bag mx-auto block size-12 text-lais-primary"
      />
      <h2 class="mt-4 text-[18px] font-semibold text-lais-text">
        Seu catálogo está vazio
      </h2>
      <p
        class="mx-auto mt-2 max-w-lg text-[13px] leading-6 text-lais-text-body"
      >
        Cadastre o primeiro produto para começar a exibi-lo no WhatsApp.
      </p>
      <LaisPrimaryButton
        class="mt-5"
        icon="i-lucide-plus"
        :disabled="!canManage"
        @click="router.push(createRoute)"
      >
        Adicionar primeiro produto
      </LaisPrimaryButton>
    </div>

    <div
      v-if="products.length || hasPreviousPage"
      class="mt-5 flex flex-col gap-3 rounded-lais border border-lais-border-soft bg-white p-4 sm:flex-row sm:items-center sm:justify-between"
    >
      <p class="text-[12px] text-lais-text-muted">
        Página <strong class="text-lais-text">{{ currentPage }}</strong>
        <span class="mx-1">·</span>
        {{ products.length }} de até {{ pageSize }} anúncios
      </p>
      <div class="flex items-center gap-2">
        <button
          type="button"
          class="inline-flex h-10 items-center gap-2 rounded-lais-sm border border-lais-border bg-white px-4 text-[12px] font-semibold text-lais-primary hover:border-lais-primary/50 disabled:cursor-not-allowed disabled:opacity-50"
          :disabled="isLoadingProducts || !hasPreviousPage"
          @click="goToPreviousPage"
        >
          <span class="i-lucide-chevron-left size-4" />
          Anterior
        </button>
        <button
          type="button"
          class="inline-flex h-10 items-center gap-2 rounded-lais-sm bg-lais-primary px-4 text-[12px] font-semibold text-white hover:bg-lais-primary-600 disabled:cursor-not-allowed disabled:opacity-50"
          :disabled="isLoadingProducts || !hasNextPage"
          @click="goToNextPage"
        >
          Próxima
          <span class="i-lucide-chevron-right size-4" />
        </button>
      </div>
    </div>
  </LaisOperationalPage>
</template>
